// filepath: apps/web/src/features/proc/FailedInstanceRow.tsx
//
// FR-UI-PROC-1 AC-3 / AC-4 / AC-5 — single-row renderer for the
// failed-instance dashboard.
//
// Renders the full FR-OBS-6 AC-2 entry shape:
//   - instance id
//   - process definition id + pinned version
//   - failed node
//   - failure reason (raw string verbatim; explicit UX treatment
//     for `restored_orphan` via <RestoredOrphanBadge />)
//   - inline failure trace summary (count of step-log entries)
//   - retry count
//   - retry-step + cancel-instance action buttons
//
// INVARIANTS:
//   - INV-2 / FR-AUTH-7: server-stripped fields are absent from the
//     response — the renderer never iterates the definition to
//     infer existence.
//   - INV-9: zero `dangerouslySetInnerHTML`. Every server-supplied
//     string renders as a React text node.
//   - FR-UI-CORE-9: `data-state` attributes on the row + the
//     `restored_orphan` badge make state machine-readable for
//     tests + a11y tooling without relying on color.
//   - FR-UI-NFR-2: failure_trace is rendered as a count + a link
//     to the trace viewer; the SPA never parses the entries as
//     anything other than data (the full trace viewer renders
//     them on demand).

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { RestoredOrphanBadge } from "./RestoredOrphanBadge";
import { RetryStepButton } from "./RetryStepButton";
import { CancelInstanceButton } from "./CancelInstanceButton";
import type { FailedInstance } from "./types";

export interface FailedInstanceRowProps {
  /** The failed instance entry to render. */
  instance: FailedInstance;
  /**
   * Called when a mutation (retry-step / cancel) succeeds. The
   * parent (dashboard) typically invalidates the list query.
   */
  onMutated?: () => void;
  /**
   * Optional click-through to the per-instance trace page. When
   * supplied, the "View trace" affordance becomes a real link.
   */
  onViewTrace?: (instanceId: string) => void;
  /**
   * Called when the server returns 409 `not_failed` on retry-step.
   * The parent re-renders the FR-UI-CORE-10 stale-version state.
   */
  onStaleVersion?: () => void;
}

export function FailedInstanceRow({
  instance,
  onMutated,
  onViewTrace,
  onStaleVersion,
}: FailedInstanceRowProps): React.ReactElement {
  const { t } = useTranslation();

  const traceCount = instance.failure_trace.length;

  return (
    <article
      data-testid="failed-instance-row"
      data-instance-id={instance.id}
      data-state="failed"
      className="failed-instance-row"
      aria-labelledby={`failed-instance-row-${instance.id}-title`}
    >
      <header className="failed-instance-row-header">
        <h3
          id={`failed-instance-row-${instance.id}-title`}
          className="failed-instance-row-title"
        >
          {instance.id}
        </h3>
        <p className="failed-instance-row-meta">
          <span data-testid="failed-instance-row-process">
            {instance.process_definition_id} (v
            {instance.pinned_definition_version})
          </span>
        </p>
      </header>

      <dl className="failed-instance-row-fields">
        <div className="failed-instance-row-field">
          <dt className="failed-instance-row-label">
            {t("failed_instance.failed_node_label", "Failed node")}
          </dt>
          <dd
            data-testid="failed-instance-row-failed-node"
            className="failed-instance-row-value"
          >
            {instance.failed_node}
          </dd>
        </div>

        <div className="failed-instance-row-field">
          <dt className="failed-instance-row-label">
            {t("failed_instance.reason_label", "Reason")}
          </dt>
          <dd
            data-testid="failed-instance-row-reason"
            className="failed-instance-row-value"
          >
            <span>{instance.failure_reason}</span>
            <RestoredOrphanBadge reason={instance.failure_reason} />
          </dd>
        </div>

        <div className="failed-instance-row-field">
          <dt className="failed-instance-row-label">
            {t("failed_instance.retries_label", "Retries attempted")}
          </dt>
          <dd
            data-testid="failed-instance-row-retries"
            className="failed-instance-row-value"
          >
            {instance.retries_attempted}
          </dd>
        </div>

        <div className="failed-instance-row-field">
          <dt className="failed-instance-row-label">
            {t("failed_instance.trace_label", "Failure trace")}
          </dt>
          <dd
            data-testid="failed-instance-row-trace"
            className="failed-instance-row-value"
          >
            <span>
              {t("failed_instance.trace_summary", "{{count}} step entries", {
                count: traceCount,
              })}
            </span>
            {onViewTrace !== undefined && (
              <>
                {" "}
                <button
                  type="button"
                  data-testid="failed-instance-row-view-trace"
                  className="failed-instance-row-link"
                  onClick={() => onViewTrace(instance.id)}
                >
                  {t("failed_instance.view_trace", "View trace")}
                </button>
              </>
            )}
          </dd>
        </div>
      </dl>

      <footer className="failed-instance-row-actions">
        <RetryStepButton
          instance={instance}
          onSuccess={onMutated}
          onConflict={onStaleVersion}
        />
        <CancelInstanceButton instance={instance} onSuccess={onMutated} />
      </footer>
    </article>
  );
}
